import React, { useCallback } from 'react';

import { ModalManager } from '@placer-ui/components';
import type { ModalManagerProps } from '@placer-ui/components/dist/core-components/modal/modal-manager/modal-manager';
import { ResolverProcessFailedModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/flow-config/resolver-process-failed-modal/resolver-process-failed-modal';
import { RequestProperty } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-property/request-property';
import type {
    SupportRequestsData,
    SupportRequestType,
} from 'features/offline-reports/support-requests/components/support-requests-modals/types';
import { SupportRequestsAskSupportModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/ask-support/support-requests-ask-support';
import { AdvanceRequestProperty } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/advance-request-property/advance-request-property';
import { RequestsTypeSelection } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-type-selection/requests-type-selection';
import { RequestRegion } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-region/request-region';
import { RequestChain } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-chain/request-chain';
import { RequestTrafficPin } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-traffic-pin/request-traffic-pin';
import { SupportRequestsSuccessModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/support-success-modal/support-success-modal';
import { SupportRequestsMessageModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-message/support-requests-message-modal';
import { SupportRequestsFailedModal } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/support-failed-modal/support-failed-modal';
import {
    InfoRequestProps,
    SupportRequestsInfoModal,
} from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-info/support-requests-info-modal';
import { SuggestEdit } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/suggest-edit/suggest-edit';
import { useSendSupportSplunkEvent } from 'features/offline-reports/support-requests/hooks/use-send-support-splunk-event';
import { RequestMarket } from 'features/offline-reports/support-requests/components/support-requests-modals/modals/request-new-location/request-market/request-market';
import { useResetSupportState } from 'features/offline-reports/support-requests/hooks/use-reset-support-state';
import { useTraceSupportQueryParam } from 'features/offline-reports/support-requests/hooks/use-trace-support-query-param';

const modalManager = ModalManager.getInstance();

type OpenSupportRequestProps<T extends SupportRequestType> = {
    flowType: T;
    retryType?: SupportRequestType;
    props?: SupportRequestsData[T];
};

export const useOpenSupportRequest = <T extends SupportRequestType>() => {
    const sendSplunkEvent = useSendSupportSplunkEvent();
    const { setSupportRequestInQuery, clearSupportRequestInQuery } = useTraceSupportQueryParam();
    const { resetState } = useResetSupportState();

    const openSupportRequest = useCallback(
        ({ retryType, props, flowType }: OpenSupportRequestProps<T>) => {
            const defaultModalProps: Partial<ModalManagerProps> = {
                hasBackdrop: true,
                maxHeight: 'unset',
                disableClickOutside: true,
                onOpenChange(open: boolean) {
                    if (!open) {
                        clearSupportRequestInQuery();
                        resetState();
                        sendSplunkEvent({
                            action: 'button-click',
                            value: `close ${flowType}`,
                        });
                    }
                },
            };

            if (flowType) {
                setSupportRequestInQuery({ type: flowType, ...props });
            }

            switch (flowType) {
                case 'region':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'region',
                        testId: 'support-request-region-modal',
                        children: <RequestRegion />,
                    });
                    break;
                case 'market':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'region',
                        testId: 'market-request',
                        children: <RequestMarket />,
                    });
                    break;
                case 'typeSelection':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'typeSelection',
                        testId: 'support-type-selection-modal',
                        children: <RequestsTypeSelection />,
                    });
                    break;
                case 'property':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'property',
                        testId: 'support-request-property-modal',
                        children: <RequestProperty />,
                    });
                    break;
                case 'askSupport':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'askSupport',
                        testId: 'support-ask-support-modal',
                        children: <SupportRequestsAskSupportModal />,
                    });
                    break;
                case 'resolverProcessFailed':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'resolverProcessFailed',
                        testId: 'support-resolver-process-failed-modal',
                        children: <ResolverProcessFailedModal retryModalType="property" />,
                    });
                    break;
                case 'advancedPropertyPolygon':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'advancedPropertyPolygon',
                        testId: 'advanced-property-polygon-modal',
                        children: <AdvanceRequestProperty requestType="advancedPropertyPolygon" />,
                    });
                    break;

                case 'advancedPropertySitePlan':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'advancedPropertyPolygon',
                        testId: 'support-advanced-property-site-plan-modal',
                        children: <AdvanceRequestProperty requestType="advancedPropertySitePlan" />,
                    });
                    break;
                case 'chain':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'chain',
                        testId: 'support-chain-modal',
                        children: <RequestChain />,
                    });
                    break;

                case 'trafficPin':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'trafficPin',
                        testId: 'support-traffic-pin-modal',
                        children: (
                            <RequestTrafficPin {...(props as SupportRequestsData['trafficPin'])} />
                        ),
                    });
                    break;

                case 'suggestEdit':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'suggestEdit',
                        testId: 'support-suggest-edit-modal',
                        children: (
                            <SuggestEdit {...(props as SupportRequestsData['suggestEdit'])} />
                        ),
                    });
                    break;
                case 'success':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'success',
                        testId: 'support-success-modal',
                        children: <SupportRequestsSuccessModal />,
                    });
                    break;
                case 'failed':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'failed',
                        testId: 'support-failed-modal',
                        children: (
                            <SupportRequestsFailedModal {...props} retryModalType={retryType!} />
                        ),
                    });
                    break;
                case 'message':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'message',
                        testId: 'support-message-modal',
                        children: <SupportRequestsMessageModal />,
                    });
                    break;
                case 'info':
                    modalManager.replaceModal({
                        ...defaultModalProps,
                        type: 'modal-template',
                        id: 'info',
                        testId: 'support-info-modal',
                        children: <SupportRequestsInfoModal {...(props as InfoRequestProps)} />,
                    });
                    break;
            }
        },
        [clearSupportRequestInQuery, resetState, sendSplunkEvent, setSupportRequestInQuery],
    );

    const closeSupportRequest = useCallback(
        (type?: SupportRequestType) => {
            clearSupportRequestInQuery();

            type ? modalManager.removeModal(type) : modalManager.removeAllModals();
        },
        [clearSupportRequestInQuery],
    );

    return {
        openSupportRequest,
        closeSupportRequest,
    };
};
